import React, { useCallback, useMemo } from "react";
import { useCurrentFrame, useCurrentScale, useVideoConfig } from "remotion";
import { ResizeHandle } from "@/components/editor/components/selection/resize-handle";
import { Overlay, OverlayType, TextOverlay } from "@/components/editor/types";
import { RotateHandle } from "@/components/editor/components/selection/rotate-handle";
import { MAX_ROWS } from "@/components/editor/constants";
import { getLayerStyles } from "../overlays/text/text-layer-content";

/**
 * SelectionOutline is a component that renders a draggable, resizable outline around selected overlays.
 * It provides visual feedback and interaction handles for manipulating overlay elements.
 *
 * @component
 * @param {Object} props
 * @param {Overlay} props.overlay - The overlay object containing position, size, and other properties
 * @param {Function} props.changeOverlay - Callback to update overlay properties
 * @param {Function} props.setSelectedOverlayId - Function to update the currently selected overlay
 * @param {number|null} props.selectedOverlayId - ID of the currently selected overlay
 * @param {boolean} props.isDragging - Whether the overlay is currently being dragged
 */
export const SelectionOutline: React.FC<{
  overlay: Overlay;
  changeOverlay: (
    overlayId: number,
    updater: (overlay: Overlay) => Overlay
  ) => void;
  setSelectedOverlayId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedOverlayId: number | null;
  isDragging: boolean;
/** Function to set the currently active panel type */
  setActivePanel: (type: OverlayType) => void;
  /** Function to set the panel state open/close */
  setSidebarIsOpen: (open: boolean) => void;
}> = ({
  overlay,
  changeOverlay,
  setSelectedOverlayId,
  selectedOverlayId,
  isDragging,
  setActivePanel,
  setSidebarIsOpen,
}) => {
  const scale = useCurrentScale();
  const scaledBorder = Math.ceil(1 / scale);

  const [hovered, setHovered] = React.useState(false);

  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);

  const isSelected = overlay.id === selectedOverlayId;

  const style: React.CSSProperties = useMemo(() => {
    // Make selected items have the highest z-index
    const baseZIndex = (MAX_ROWS - (overlay.row || 0)) * 10;
    const selectionBoost = isSelected ? 1000 : 1000;
    const zIndex = baseZIndex + selectionBoost;
  
    return {
      width: overlay.width,
      height: overlay.height,
      left: overlay.left,
      top: overlay.top,
      position: "absolute",
      outline:
        (hovered && !isDragging) || isSelected
          ? `${scaledBorder}px solid #3B8BF2`
          : undefined,
      transform: `rotate(${overlay.rotation || 0}deg)`,
      transformOrigin: "center center",
      userSelect: "none",
      touchAction: "none",
      zIndex,
      pointerEvents: hovered || isDragging ? "all" : isSelected ? "none" : "all", // Ensure hovered items always allow pointer events
    };
  }, [overlay, hovered, isDragging, isSelected, scaledBorder, overlay.row]);

  const startDragging = useCallback(
    (e: PointerEvent | React.MouseEvent) => {
      const initialX = e.clientX;
      const initialY = e.clientY;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        const offsetX = (pointerMoveEvent.clientX - initialX) / scale;
        const offsetY = (pointerMoveEvent.clientY - initialY) / scale;
        changeOverlay(overlay.id, (o) => {
          return {
            ...o,
            left: Math.round(overlay.left + offsetX),
            top: Math.round(overlay.top + offsetY),
            isDragging: true,
          };
        });
      };

      const onPointerUp = () => {
        changeOverlay(overlay.id, (o) => {
          return {
            ...o,
            isDragging: false,
          };
        });
        window.removeEventListener("pointermove", onPointerMove);
      };

      window.addEventListener("pointermove", onPointerMove, { passive: true });

      window.addEventListener("pointerup", onPointerUp, {
        once: true,
      });
    },
    [overlay, scale, changeOverlay]
  );

  const onPointerDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.button !== 0) {
        return;
      }

      setSelectedOverlayId(overlay.id);

      if (
        overlay.type === OverlayType.VIDEO ||
        overlay.type === OverlayType.TEXT ||
        overlay.type === OverlayType.SOUND ||
        overlay.type === OverlayType.CAPTION ||
        overlay.type === OverlayType.IMAGE ||
        overlay.type === OverlayType.LOTTIE
      ) {
        setActivePanel(overlay.type);
        setSidebarIsOpen(true);
      }

      startDragging(e);
    },
    [overlay.id, setSelectedOverlayId, startDragging]
  );

  if (overlay.type === OverlayType.SOUND) {
    return null;
  }

  const [textContent, setTextContent ] = React.useState("");
  if (overlay.type === OverlayType.TEXT) {
    React.useEffect(()=>{
      setTextContent(overlay.content)
    },[isSelected, overlay.isDragging]);
  }

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerEnter={onMouseEnter}
      onPointerLeave={onMouseLeave}
      style={style}
    >
      {overlay.type === OverlayType.TEXT && isSelected && (
        (() => {

          
          const { containerStyle, textStyle } = getLayerStyles({overlay, frame, fps});

          const handleTextChange = (e: React.FormEvent<HTMLDivElement>) => {
            e.preventDefault();
            const newContent = e.currentTarget.innerText || "" as any;
            changeOverlay(overlay.id, (o) => {
              return {
                ...o,
                content: newContent
              };
            });
          }

         

          return (
          <div style={{ ...containerStyle, position: "absolute", zIndex: (style.zIndex as number) + 1 }}>
              <div onInput={handleTextChange} aria-disabled suppressContentEditableWarning contentEditable={true} style={{...textStyle, pointerEvents: "all", color: "rgba(255, 255, 255, 0)", background: "transparent", boxShadow: "none", textShadow: "none"}}>{textContent}</div>
          </div>
          );
        })()
      )}
      {isSelected ? (
        <>
          <ResizeHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="top-left"
          />
          <ResizeHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="top-right"
          />
          <ResizeHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="bottom-left"
          />
          <ResizeHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="bottom-right"
          />
          <RotateHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="top-left"
          />
          <RotateHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="top-right"
          />
          <RotateHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="bottom-left"
          />
          <RotateHandle
            overlay={overlay}
            setOverlay={changeOverlay}
            scale={scale}
            type="bottom-right"
          />
        </>
      ) : null}
    </div>
  );
};
